/**
 * 판매 줄 칸 그리기 — 판매 등록(/sales/new)과 판매 상세(/sales/:id)의 품목 추가가 같이 쓴다(2026-11-03 판매 상세).
 * 제품 찾기 줄(제품 × 컨디션) · 구성 칩 · 컨디션 고르기 · 재고 칩. DirectSaleCreateView 에서 옮겼다(내용 그대로).
 */
import type { DirectSaleProductMatch } from '../../api/client';
import { escapeHtml, fmtNumber } from '../../lib/cells';
import { CONDITION_OPTIONS } from '../../lib/condition';
import type { DirectSaleFormLine, DirectSaleMatchRow } from './types';

/** 칩 — 구성 줄이 있으면 세트 · 서버 구성, 단품은 칩 없음 */
export function configChipOf(product: DirectSaleProductMatch): string {
  if (!product.is_config) return '';
  return `<span class="chip">${product.product_type === 'set' ? '세트' : '서버 구성'}</span> `;
}

/** 찾기 줄의 컨디션 글 — "NIB · 가용 3", 재고 없는 줄은 "가용 0 — 컨디션 고르기" */
export function matchRowConditionText(row: DirectSaleMatchRow): string {
  return row.condition === '' ? '가용 0 — 컨디션 고르기' : `${row.condition} · 가용 ${row.available}`;
}

/** 줄의 재고 상태 — 고른 컨디션의 가용 수와 수량으로. none = 제품에 재고가 아예 없음 · 그 컨디션 가용 0 / unset = 재고는 있는데 컨디션 안 고름 · short = 수량보다 적음 */
export type StockState = { kind: 'unset' } | { kind: 'none' } | { kind: 'enough'; available: number } | { kind: 'short'; available: number; shortage: number };

export function stockStateOf(line: DirectSaleFormLine): StockState {
  if (line.product.available.length === 0) return { kind: 'none' };
  if (line.condition === '') return { kind: 'unset' };
  const row = line.product.available.find((candidate) => candidate.condition === line.condition);
  if (row === undefined) return { kind: 'none' };
  const quantity = Number.isFinite(line.quantity) ? line.quantity : 0;   // 비운 수량은 0 — 모자람을 못 센다
  return row.available >= quantity ? { kind: 'enough', available: row.available } : { kind: 'short', available: row.available, shortage: quantity - row.available };
}

/** 컨디션 칸 아래 칩 — 재고 n(초록) · 재고 n · 부족 m → 구매 요청(주황) · 재고 없음 → 구매 요청(주황). 안 골랐으면 '' */
export function renderStockChip(line: DirectSaleFormLine): string {
  const state = stockStateOf(line);
  switch (state.kind) {
    case 'unset':  return '';
    case 'enough': return `<span class="ds-stock ds-stock-enough">재고 ${fmtNumber(state.available)}</span>`;
    case 'short':  return `<span class="ds-stock ds-stock-short">재고 ${fmtNumber(state.available)} · 부족 ${fmtNumber(state.shortage)} → 구매 요청</span>`;
    case 'none':   return '<span class="ds-stock ds-stock-short">재고 없음 → 구매 요청</span>';
    default: {
      const unreachable: never = state;
      throw new Error(`unhandled stock state: ${JSON.stringify(unreachable)}`);
    }
  }
}

/** 컨디션 고르기 — CONDITION_OPTIONS + 목록에 없는 가용 컨디션 · 지금 줄의 컨디션(다시 등록이 가져온 값).
 *  "재고 있음" · "재고 없음 — 구매 요청" 두 묶음(가용 수는 아래 칩). 재고 없는 것도 고를 수 있다(니즈가 된다) */
export function conditionOptionsOf(line: DirectSaleFormLine): string {
  const known: readonly string[] = CONDITION_OPTIONS;
  const extras = [...line.product.available.map((row) => row.condition), line.condition]
    .filter((condition, index, all) => condition !== '' && !known.includes(condition) && all.indexOf(condition) === index);
  const conditions = [...known, ...extras];
  const optionOf = (condition: string): string => `<option value="${escapeHtml(condition)}"${condition === line.condition ? ' selected' : ''}>${escapeHtml(condition)}</option>`;   // 수는 아래 칩이 말한다
  const inStock = line.product.available.map((row) => optionOf(row.condition)).join('');
  const outOfStock = conditions.filter((condition) => !line.product.available.some((row) => row.condition === condition)).map(optionOf).join('');
  return `${inStock === '' ? '' : `<optgroup label="재고 있음">${inStock}</optgroup>`}<optgroup label="재고 없음 — 구매 요청">${outOfStock}</optgroup>`;
}

/** 제품 목록 → 찾기 줄. 가용 컨디션마다 한 줄, 없으면 컨디션 빈 한 줄. 순서는 서버 순서 그대로 */
export function matchRowsOf(products: readonly DirectSaleProductMatch[]): DirectSaleMatchRow[] {
  return products.flatMap((product) => product.available.length === 0
    ? [{ product, condition: '', available: 0 }]
    : product.available.map((row) => ({ product, condition: row.condition, available: row.available })));
}
